import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import Joi from "joi-browser";
import { getTotalHours } from "../../services/appService";
import { getAlert, getConfirmAlert } from "../../services/appAlerts";
import Input from "../../comman/input";
import TextArea from "../../comman/textarea";
import { updateCandidateInvoiceReview } from "../../app/store/candidate";

const schema = {
  status: Joi.boolean().required().label("Review Decision"),
};

function InvoiceReview(props) {
  const {
    selectedObj,
    invType,
    timeLogInvoices,
    candidateObj,
    getInvoiceAmount,
    entType,
    invRevShowHide,
    handleInvRevShowHide,
  } = props;

  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedObj !== undefined && selectedObj !== "") {
      const obj = selectedObj;
      const nextData = {};
      nextData["status"] = "";
      nextData["reason"] = "";
      nextData["adjustmentPlus"] = "";
      nextData["adjustmentMinues"] = "";

      let candidateRate = 0;
      let invoType = invType;
      if (invType === "subcontractor") {
        if (entType === "C2C Basis" && candidateObj?.rates?.subcontractorRate) {
          candidateRate = candidateObj.rates.subcontractorRate;
        } else {
          if (candidateObj?.rates?.candidateRate)
            candidateRate = candidateObj.rates.candidateRate;

          invoType = "candidate";
        }
      }

      if (invType === "recruiter" && candidateObj.rates?.recruiterRate)
        candidateRate = candidateObj.rates.recruiterRate;
      if (invType === "agency" && candidateObj.rates?.agencyRate)
        candidateRate = candidateObj.rates.agencyRate;

      let fAmt = 0;
      if (invType === "subcontractor") {
        fAmt = parseFloat(getInvoiceAmount(obj));
      } else {
        fAmt = parseFloat(getTotalHours(obj?.workingHrs) * candidateRate);
      }
      let baselineAmt = fAmt;
      const invItems = timeLogInvoices.filter((x) => x.timeLogId === obj.id);

      if (invItems.length > 0) {
        const selectedInv = invItems[0];
        if (selectedInv?.adpDeduction > 0) {
          baselineAmt += selectedInv.adpDeduction;
          fAmt += selectedInv.adpDeduction;
        }

        if (selectedInv?.adjustmentMinues > 0)
          fAmt -= selectedInv.adjustmentMinues;
        if (selectedInv?.adjustmentPlus > 0) fAmt += selectedInv.adjustmentPlus;

        nextData["status"] = selectedInv.status;
        nextData["reason"] = selectedInv.reason;
        nextData["adjustmentPlus"] =
          selectedInv.adjustmentPlus?.toString() ?? "";
        nextData["adjustmentMinues"] =
          selectedInv.adjustmentMinues?.toString() ?? "";
      }
      nextData["id"] = obj.id;
      nextData["finalAmt"] = parseFloat(fAmt).toFixed(2);
      nextData["docUploadType"] = invoType;
      nextData["rate"] = candidateRate;
      nextData["baselineAmt"] = baselineAmt;
      setData(nextData);
      setErrors({});
    } else {
      setData({});
      setErrors({});
    }
  }, [
    selectedObj,
    invType,
    timeLogInvoices,
    candidateObj,
    getInvoiceAmount,
    entType,
  ]);

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    if (schema[name] === undefined) return null;
    const fieldSchema = { [name]: schema[name] };
    const { error } = Joi.validate(obj, fieldSchema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false, allowUnknown: true };
    const result = Joi.validate(data, schema, options);
    if (!result.error) return null;
    const validationErrors = {};
    for (const item of result.error.details) {
      validationErrors[item.path[0]] = item.message;
    }
    return Object.keys(validationErrors).length > 0 ? validationErrors : null;
  };

  const handleChange = ({ currentTarget: input }) => {
    const nextErrors = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) nextErrors[input.name] = errorMessage;
    else delete nextErrors[input.name];

    const nextData = { ...data };
    nextData[input.name] = input.value;

    if (input.name === "adjustmentPlus" || input.name === "adjustmentMinues") {
      const baseline = parseFloat(nextData.baselineAmt) || 0;
      const plus = parseFloat(nextData.adjustmentPlus) || 0;
      const minus = parseFloat(nextData.adjustmentMinues) || 0;
      nextData["finalAmt"] = parseFloat(baseline + plus - minus).toFixed(2);
    }

    setData(nextData);
    setErrors(nextErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isProcessing) return;
    const formErrors = validate();
    setErrors(formErrors || {});
    if (formErrors) return;

    const result = await getConfirmAlert(
      "Warning",
      "Are you sure you want to save Timelog?",
      "warning",
    );
    if (result.isConfirmed) {
      setIsProcessing(true);
      try {
        await dispatch(updateCandidateInvoiceReview(data));
        handleInvRevShowHide("save");
        getAlert("Timelog Saved Successfully", "", "success");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const renderInput = (name, label, type = "text", placeholder = "") => (
    <Input
      type={type}
      name={name}
      value={data[name] || ""}
      label={label}
      placeholder={placeholder || label}
      onChange={handleChange}
      error={errors[name]}
      req={schema[name]}
    />
  );

  const renderTextArea = (name, label, placeholder = "") => (
    <TextArea
      name={name}
      value={data[name] || ""}
      label={label}
      placeholder={placeholder || label}
      onChange={handleChange}
      error={errors[name]}
      req={schema[name]}
    />
  );

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .modal-backdrop { z-index: 1080 !important; }
        .modal { z-index: 1090 !important; overflow-y: hidden !important; }
        .modal-backdrop.show { backdrop-filter: blur(8px) !important; -webkit-backdrop-filter: blur(8px) !important; background-color: rgba(15, 23, 42, 0.45) !important; opacity: 1 !important; }
        .modal.fade .wf-review-modal.modal-dialog { transform: scale(0.96) !important; transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) !important; }
        .modal.show .wf-review-modal.modal-dialog { transform: scale(1) !important; }
        
        .wf-review-modal { 
          max-width: 680px !important; 
          width: 100% !important; 
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        }
        
        .wf-review-modal .modal-content { 
          border-radius: 0 !important; 
          border: 1px solid #cbd5e1 !important; 
          box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25) !important; 
          overflow: hidden !important; 
          background: #ffffff !important; 
        }
        
        .wf-review-modal .modal-header { 
          position: relative !important; 
          display: block !important; 
          padding: 14px 24px !important; 
          background: #ffffff !important; 
          border-bottom: 1px solid #edf2f7 !important; 
          overflow: hidden !important; 
        }
        .wf-review-modal .modal-header::before { display: none !important; }
        
        .wf-review-modal .rt-header-shell { display: flex !important; flex-direction: column !important; gap: 0 !important; width: 100% !important; }
        .wf-review-modal .rt-header-top { display: flex !important; justify-content: space-between !important; align-items: center !important; gap: 16px !important; width: 100% !important; }
        .wf-review-modal .rt-header-main { display: flex !important; align-items: center !important; gap: 14px !important; min-width: 0 !important; flex: 1 1 auto !important; }
        .wf-review-modal .rt-header-copy { display: flex !important; flex-direction: column !important; gap: 2px !important; min-width: 0 !important; }
        
        .wf-review-modal .rt-modal-title { 
          display: inline-flex !important; 
          align-items: center !important; 
          margin: 0 !important; 
          color: #0f172a !important; 
          font-size: 17px !important; 
          font-weight: 600 !important; 
          line-height: 1.25 !important; 
        }
        
        .wf-review-modal .rt-header-icon { 
          width: 38px !important; 
          height: 38px !important; 
          min-height: 38px !important; 
          border-radius: 0 !important; 
          display: inline-flex !important; 
          align-items: center !important; 
          justify-content: center !important; 
          color: #171717 !important; 
          background: #f5f5f5 !important; 
          border: 1px solid #d4d4d4 !important; 
          box-shadow: 0 2px 5px rgba(15,23,42,0.06) !important; 
          font-size: 16px !important; 
          flex-shrink: 0 !important; 
        }
        
        .wf-review-modal .rt-modal-title-text { display: inline-flex !important; align-items: center !important; gap: 8px !important; flex-wrap: wrap !important; }
        .wf-review-modal .rt-modal-title-badge { 
          display: inline-flex !important; 
          align-items: center !important; 
          justify-content: center !important; 
          padding: 3px 8px !important; 
          border-radius: 0 !important; 
          font-size: 10px !important; 
          font-weight: 700 !important; 
          text-transform: uppercase !important; 
          letter-spacing: 0.04em !important; 
          background: #171717 !important; 
          color: #ffffff !important; 
          border: 1px solid #171717 !important; 
        }
        
        .wf-review-modal .rt-modal-desc { 
          display: inline-flex !important; 
          align-items: center !important; 
          gap: 6px !important; 
          color: #64748b !important; 
          font-weight: 500 !important; 
          line-height: 1.4 !important; 
          font-size: 12px !important; 
        }
        .wf-review-modal .rt-modal-desc i { color: #64748b !important; }
        
        .wf-review-modal .rt-action-btn { 
          display: inline-flex !important; 
          align-items: center !important; 
          justify-content: center !important; 
          width: 32px !important; 
          height: 32px !important; 
          padding: 0 !important; 
          font-size: 14px !important; 
          color: #64748b !important; 
          background: #ffffff !important; 
          border: 1px solid #cbd5e1 !important; 
          border-radius: 0 !important; 
          cursor: pointer !important; 
          transition: all 0.2s ease !important; 
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.06) !important; 
          outline: none !important; 
        }
        .wf-review-modal .rt-action-btn:hover { 
          color: #ef4444 !important; 
          border-color: #fca5a5 !important; 
          background: #fef2f2 !important; 
          transform: translateY(-1px) !important; 
        }
        
        .wf-review-modal .modal-body { 
          padding: 24px !important; 
          background: #ffffff !important; 
        }
        
        .wf-review-modal .form-group { margin-bottom: 16px !important; }
        
        .wf-review-modal label.form-control-label { 
          display: block !important; 
          font-size: 12px !important; 
          font-weight: 600 !important; 
          color: #334155 !important; 
          margin-bottom: 6px !important; 
          letter-spacing: 0.01em !important; 
        }
        
        .wf-review-modal .form-control { 
          background: #ffffff !important; 
          border: 1px solid #cbd5e1 !important; 
          border-radius: 0 !important; 
          padding: 8px 12px !important; 
          font-size: 13.5px !important; 
          font-weight: 500 !important; 
          color: #0f172a !important; 
          transition: all 0.2s ease !important; 
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important; 
          outline: none !important; 
        }
        .wf-review-modal .form-control:focus { 
          border-color: #2563eb !important; 
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important; 
        }
        .wf-review-modal .form-control::placeholder {
          color: #94a3b8 !important;
          font-weight: 400 !important;
        }
        .wf-review-modal textarea.form-control { 
          min-height: 76px !important; 
          resize: vertical !important; 
        }
        
        .wf-review-modal .wf-section-label { 
          font-size: 12px !important; 
          font-weight: 700 !important; 
          color: #1e293b !important; 
          letter-spacing: 0.03em !important; 
          text-transform: uppercase !important; 
          margin-bottom: 10px !important; 
          display: block !important; 
        }
        
        /* Status Card Group */
        .wf-review-modal .wf-status-cards-group { 
          display: grid !important; 
          grid-template-columns: 1fr 1fr !important; 
          gap: 12px !important; 
          width: 100% !important; 
          margin-bottom: 20px !important; 
        }
        .wf-review-modal .wf-status-radio-input { display: none !important; }
        
        .wf-review-modal .wf-status-card { 
          display: flex !important; 
          align-items: center !important; 
          gap: 12px !important; 
          padding: 12px 16px !important; 
          border-radius: 0 !important; 
          border: 1px solid #cbd5e1 !important; 
          background: #ffffff !important; 
          cursor: pointer !important; 
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; 
          margin-bottom: 0 !important; 
          user-select: none !important; 
          width: 100% !important; 
          position: relative !important;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04) !important;
        }
        .wf-review-modal .wf-status-card:hover { 
          border-color: #94a3b8 !important; 
          background: #f8fafc !important; 
          transform: translateY(-1px) !important; 
          box-shadow: 0 4px 8px rgba(15, 23, 42, 0.06) !important; 
        }
        
        .wf-review-modal .wf-status-card-icon { 
          width: 32px !important;
          height: 32px !important;
          border-radius: 0 !important;
          display: inline-flex !important; 
          align-items: center !important; 
          justify-content: center !important; 
          font-size: 15px !important; 
          flex-shrink: 0 !important;
        }
        
        .wf-review-modal .approved-card .wf-status-card-icon { 
          color: #16a34a !important; 
          background: #f0fdf4 !important;
          border: 1px solid #bbf7d0 !important;
        }
        .wf-review-modal .rejected-card .wf-status-card-icon { 
          color: #e11d48 !important; 
          background: #fff1f2 !important;
          border: 1px solid #fecdd3 !important;
        }
        
        .wf-review-modal .wf-status-card-text { 
          display: flex !important; 
          flex-direction: column !important; 
          gap: 1px !important; 
          min-width: 0 !important;
        }
        .wf-review-modal .wf-status-card-title { 
          font-size: 14px !important; 
          font-weight: 700 !important; 
          color: #0f172a !important; 
          letter-spacing: 0.01em !important;
        }
        .wf-review-modal .wf-status-card-desc { 
          font-size: 11.5px !important; 
          color: #64748b !important; 
          font-weight: 400 !important; 
          line-height: 1.3 !important;
        }
        
        .wf-review-modal .wf-status-radio-input:checked + .approved-card { 
          border-color: #16a34a !important; 
          background: #f0fdf4 !important; 
          box-shadow: 0 0 0 1px #16a34a, 0 4px 10px rgba(22, 163, 74, 0.08) !important; 
        }
        .wf-review-modal .wf-status-radio-input:checked + .approved-card .wf-status-card-title {
          color: #15803d !important;
        }
        
        .wf-review-modal .wf-status-radio-input:checked + .rejected-card { 
          border-color: #e11d48 !important; 
          background: #fff1f2 !important; 
          box-shadow: 0 0 0 1px #e11d48, 0 4px 10px rgba(225, 29, 72, 0.08) !important; 
        }
        .wf-review-modal .wf-status-radio-input:checked + .rejected-card .wf-status-card-title {
          color: #be123c !important;
        }
        
        /* Adjustments Section */
        .wf-review-modal .wf-adjustments-card {
          background: #fafafa !important;
          border: 1px solid #e5e5e5 !important;
          padding: 14px 16px 4px !important;
          margin-bottom: 16px !important;
        }
        .wf-review-modal .wf-adjustments-head {
          font-size: 11px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.04em !important;
          color: #737373 !important;
          margin-bottom: 10px !important;
          display: flex !important;
          align-items: center !important;
          gap: 6px !important;
        }

        /* Final Amount to Pay Summary Box */
        .wf-review-modal .wf-payout-summary-card {
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-left: 4px solid #16a34a !important;
          padding: 14px 18px !important;
          margin-bottom: 16px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          gap: 16px !important;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04) !important;
        }
        .wf-review-modal .wf-payout-text-block {
          display: flex !important;
          flex-direction: column !important;
          gap: 2px !important;
        }
        .wf-review-modal .wf-payout-title {
          font-size: 12px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.04em !important;
          color: #334155 !important;
        }
        .wf-review-modal .wf-payout-subtitle {
          font-size: 11.5px !important;
          font-weight: 400 !important;
          color: #64748b !important;
        }
        .wf-review-modal .wf-payout-value-block {
          display: inline-flex !important;
          align-items: baseline !important;
          gap: 2px !important;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        }
        .wf-review-modal .wf-payout-currency {
          font-size: 16px !important;
          font-weight: 600 !important;
          color: #15803d !important;
        }
        .wf-review-modal .wf-payout-amount {
          font-size: 22px !important;
          font-weight: 700 !important;
          color: #15803d !important;
          letter-spacing: -0.02em !important;
        }

        /* Footer */
        .wf-review-modal .modal-footer { 
          background: #f8fafc !important; 
          border-top: 1px solid #edf2f7 !important; 
          padding: 14px 24px !important; 
          display: flex !important; 
          justify-content: flex-end !important; 
          gap: 10px !important; 
        }
        .wf-review-modal .wf-modal-footer-close-btn { 
          display: inline-flex !important; 
          align-items: center !important; 
          gap: 6px !important; 
          padding: 7px 14px !important; 
          font-size: 13.5px !important; 
          font-weight: 500 !important; 
          color: #1e293b !important; 
          background: #ffffff !important; 
          border: 1px solid #cbd5e1 !important; 
          border-radius: 0 !important; 
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.06) !important; 
          cursor: pointer !important; 
          transition: all 0.2s ease !important; 
          outline: none !important; 
        }
        .wf-review-modal .wf-modal-footer-close-btn:hover { 
          background: #f8fafc !important; 
          color: #0f172a !important; 
          border-color: #94a3b8 !important; 
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.1) !important; 
        }
        .wf-review-modal .wf-btn-save { 
          display: inline-flex !important; 
          align-items: center !important; 
          justify-content: center !important; 
          gap: 7px !important; 
          padding: 7px 16px !important; 
          border-radius: 0 !important; 
          border: 1px solid #2563eb !important; 
          background: #2563eb !important; 
          color: #ffffff !important; 
          font-size: 13.5px !important; 
          font-weight: 500 !important; 
          cursor: pointer !important; 
          transition: all 0.2s ease !important; 
          outline: none !important; 
          box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2) !important; 
        }
        .wf-review-modal .wf-btn-save:hover:not(:disabled) {
          background: #1d4ed8 !important; 
          border-color: #1d4ed8 !important; 
          transform: translateY(-1px) !important; 
          box-shadow: 0 3px 8px rgba(37, 99, 235, 0.28) !important; 
        }
        .wf-review-modal .wf-btn-save:active:not(:disabled) { transform: scale(0.97) !important; }
        .wf-review-modal .wf-btn-save:disabled {
          background: #cbd5e1 !important;
          border-color: #cbd5e1 !important;
          color: #64748b !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
          transform: none !important;
        }
      `,
        }}
      />

      <Modal
        show={invRevShowHide}
        size="lg"
        dialogClassName="wf-review-modal"
        centered
      >
        <Modal.Header>
          <div className="rt-header-shell">
            <div className="rt-header-top">
              <div className="rt-header-main">
                <i
                  className="rt-header-icon fa fa-check-square-o"
                  aria-hidden="true"
                />
                <div className="rt-header-copy">
                  <h2 className="rt-modal-title">
                    <span className="rt-modal-title-text">
                      <span>Time Log Review</span>
                      <span className="rt-modal-title-badge">Review</span>
                    </span>
                  </h2>
                  <span className="rt-modal-desc">
                    <i className="fa fa-info-circle" aria-hidden="true" />{" "}
                    Review timelogs, make adjustments, and approve/reject
                    transactions.
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="rt-action-btn"
                onClick={() => handleInvRevShowHide("", "")}
                title="Close"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="border-0">
          <div>
            <span className="wf-section-label">
              Review Decision <span className="text-danger">*</span>
            </span>
            <div className="wf-status-cards-group">
              <div className="wf-status-card-wrap">
                <input
                  name="status"
                  className="wf-status-radio-input"
                  id="approved"
                  type="radio"
                  value="true"
                  onChange={handleChange}
                  checked={
                    data["status"] === true || data["status"] === "true"
                  }
                />
                <label
                  className="wf-status-card approved-card"
                  htmlFor="approved"
                >
                  <div className="wf-status-card-icon">
                    <i className="fa fa-check-circle"></i>
                  </div>
                  <div className="wf-status-card-text">
                    <span className="wf-status-card-title">Approve</span>
                    <span className="wf-status-card-desc">
                      Accept timelog & finalize payment
                    </span>
                  </div>
                </label>
              </div>
              <div className="wf-status-card-wrap">
                <input
                  name="status"
                  className="wf-status-radio-input"
                  id="notApproved"
                  type="radio"
                  value="false"
                  onChange={handleChange}
                  checked={
                    data["status"] === false || data["status"] === "false"
                  }
                />
                <label
                  className="wf-status-card rejected-card"
                  htmlFor="notApproved"
                >
                  <div className="wf-status-card-icon">
                    <i className="fa fa-times-circle"></i>
                  </div>
                  <div className="wf-status-card-text">
                    <span className="wf-status-card-title">Reject</span>
                    <span className="wf-status-card-desc">
                      Flag issues or reject this invoice
                    </span>
                  </div>
                </label>
              </div>
            </div>
            {errors.status && (
              <span className="text-danger d-block mt-2">{errors.status}</span>
            )}

            <div className="wf-adjustments-card">
              <div className="wf-adjustments-head">
                <i className="fa fa-sliders" aria-hidden="true"></i> Adjustments (Optional)
              </div>
              <div className="row">
                <div className="col-md-6">
                  {renderInput("adjustmentPlus", "Adjustment Plus (+ $)", "number", "0.00")}
                </div>
                <div className="col-md-6">
                  {renderInput("adjustmentMinues", "Adjustment Minus (- $)", "number", "0.00")}
                </div>
              </div>
            </div>

            <div className="wf-payout-summary-card">
              <div className="wf-payout-text-block">
                <span className="wf-payout-title">Final Amount to Pay</span>
                <span className="wf-payout-subtitle">Calculated net payout for this time log</span>
              </div>
              <div className="wf-payout-value-block">
                <span className="wf-payout-currency">$</span>
                <span className="wf-payout-amount">
                  {Number(parseFloat(data.finalAmt || 0).toFixed(2)).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>

            <div className="form-group mb-0">
              {renderTextArea("reason", "Reason / Notes", "Add notes or reason for this review...")}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="wf-modal-footer-close-btn"
            onClick={() => handleInvRevShowHide("", "")}
          >
            <i className="fa fa-times" aria-hidden="true"></i> Close
          </button>
          <button
            className="wf-btn-save"
            onClick={handleSubmit}
            disabled={
              isProcessing ||
              ![true, false, "true", "false"].includes(data.status)
            }
          >
            <i className={`fa ${isProcessing ? "fa-spinner fa-spin" : "fa-check"}`} aria-hidden="true"></i>{" "}
            {isProcessing ? "Saving..." : "Save"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default InvoiceReview;
